import type { CategoryCode, VerificationStatus } from "@oshimap/domain";

export interface CategoryDef {
  code: CategoryCode;
  label: string;
  iconName: string;
  /** 탐색 화면 노출 순서 */
  order: number;
  /** 탐색 카테고리 그리드에 표시할지 */
  browsable: boolean;
}

export const CATEGORIES: Readonly<Record<CategoryCode, CategoryDef>> = {
  POPUP_STORE: { code:"POPUP_STORE", label:"팝업스토어", iconName:"popup",     order:1, browsable:true },
  COLLAB_CAFE: { code:"COLLAB_CAFE", label:"콜라보 카페",  iconName:"cafe-collab",order:2, browsable:true },
  GOODS_STORE: { code:"GOODS_STORE", label:"굿즈샵", iconName:"goods",     order:3, browsable:true },
  GACHA_STORE: { code:"GACHA_STORE", label:"가챠샵", iconName:"gacha",     order:4, browsable:true },
  FAN_EVENT:   { code:"FAN_EVENT",   label:"팬 이벤트", iconName:"fan-event", order:5, browsable:true },
  HOLY_PLACE:  { code:"HOLY_PLACE",  label:"성지순례", iconName:"pilgrimage",order:6, browsable:true },
  EXHIBITION:  { code:"EXHIBITION",  label:"전시", iconName:"exhibition",order:7, browsable:true },
  CONCERT:     { code:"CONCERT",     label:"공연", iconName:"concert",   order:8, browsable:true },
  CAFE:        { code:"CAFE",        label:"카페", iconName:"cafe",      order:9, browsable:false },
  DEFAULT:     { code:"DEFAULT",     label:"일반 장소", iconName:"pin",       order:99,browsable:false },
};

export const BROWSABLE_CATEGORIES: readonly CategoryDef[] =
  Object.values(CATEGORIES).filter((c) => c.browsable).sort((a, b) => a.order - b.order);

/**
 * 검증 상태를 사용자에게 뭐라고 부르는지.
 *
 * Web 은 표로, Native 는 삼항 연산으로 각자 적고 있었습니다. 그래서 Native 는
 * `PENDING`(검수 중)을 "미검증" 으로 뭉뚱그려, 검수 중인 장소를 **확인되지
 * 않은 곳처럼** 보여 주고 있었습니다. 제품 용어는 한 곳에서만 정합니다.
 */
export const VERIFICATION_LABEL: Record<VerificationStatus, string> = {
  OFFICIAL: "공식",
  VERIFIED: "검증됨",
  PENDING: "검수 중",
  UNVERIFIED: "미검증",
};

/** 모르는 값은 미검증으로 봅니다 — 확인되지 않은 것을 확인된 것처럼 부르지 않습니다. */
export function verificationLabel(status: string): string {
  return VERIFICATION_LABEL[status as VerificationStatus] ?? VERIFICATION_LABEL.UNVERIFIED;
}

/**
 * 카테고리 코드를 사람이 읽는 이름으로 바꿉니다.
 *
 * `normalizeCategoryCode` 를 쓰지 않습니다. 그쪽은 모르는 코드를 `DEFAULT`
 * 로 접어서 지도 핀에 아이콘을 붙일 수 있게 하는 것이 목적입니다. 검수
 * 화면에서 같은 짓을 하면, 출처가 우리가 모르는 분류를 보내 왔다는 **신호가
 * 사라지고** 운영자는 그것을 "일반 장소"로 읽습니다. 모르는 코드는 코드
 * 그대로 보여 주는 것이 정직합니다.
 */
export function categoryLabel(code?: string | null): string {
  if (!code) return "미확인";
  return CATEGORIES[code as CategoryCode]?.label ?? code;
}

/**
 * 출처 유형(28 §2)의 표시 이름입니다. 검수 화면에 `OFFICIAL_WEBSITE` 가
 * 그대로 찍히고 있었습니다.
 */
export const SOURCE_TYPE_LABEL: Record<string, string> = {
  OFFICIAL_API: "공식 API",
  PUBLIC_API: "공개 API",
  RSS: "RSS",
  OFFICIAL_WEBSITE: "공식 웹사이트",
  OFFICIAL_EVENT_PAGE: "공식 행사 페이지",
  OFFICIAL_STORE_PAGE: "공식 매장 페이지",
  PUBLIC_FEED: "공개 피드",
  MAP_PROVIDER: "지도 제공자",
  USER_SUBMISSION: "사용자 제보",
  MANUAL_IMPORT: "수동 입력",
  CRAWLER: "크롤러",
};

/** 모르는 유형은 코드를 그대로 보여 줍니다 — 없는 이름을 지어내지 않습니다. */
export function sourceTypeLabel(type?: string | null): string {
  if (!type) return "유형 미상";
  return SOURCE_TYPE_LABEL[type] ?? type;
}
